// PaymentsPage.jsx
import React, { useEffect, useState, useRef } from "react";
import "./PaymentsPage.css";

/**
 * PaymentsPage (updated to use explicit API_BASE and better logging)
 *
 * Expects backend at `${process.env.REACT_APP_API_URL} by default (use REACT_APP_API_BASE to override)
 */

const API_BASE = process.env.REACT_APP_API_BASE || `${process.env.REACT_APP_API_URL}`;

export default function PaymentsPage() {
  const [projects, setProjects] = useState([]);
  // selectedProject: number id or "" when none selected
  const [selectedProject, setSelectedProject] = useState("");
  const [milestones, setMilestones] = useState([]);
  const [payments, setPayments] = useState([]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const timeoutRef = useRef(null);

  const emptyForm = {
    id: null,
    project_id: null,
    milestone_id: null,
    type: "income",
    amount: "",
    payment_date: "",
    notes: "",
    method: "",
    category: "",
    status: "pending",
  };
  const [form, setForm] = useState(emptyForm);
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  function apiHeaders(json = true) {
    const headers = {};
    if (json) headers["Content-Type"] = "application/json";
    const token = localStorage.getItem("token");
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return headers;
  }

  function showMsg(text) {
    setMessage(text);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setMessage(""), 4000);
  }

  // Load projects (simple)
  async function loadProjects() {
    try {
      const res = await fetch(`${API_BASE}/api/projects`, { headers: apiHeaders(false) });
      const ct = res.headers.get("content-type") || "";
      let body = ct.includes("application/json") ? await res.json().catch(() => null) : await res.text().catch(() => null);
      console.log("loadProjects res", res.status, body);
      if (!res.ok) throw new Error(body?.error || body || "Failed to load projects");
      setProjects(Array.isArray(body) ? body : []);
    } catch (err) {
      console.error("loadProjects error:", err);
      setProjects([]);
      showMsg(`❌ ${err.message}`);
    }
  }

  // Load milestones for a project
  async function loadMilestones(projectId) {
    if (!projectId) {
      setMilestones([]);
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/api/milestones/project/${projectId}`, {
        headers: apiHeaders(false),
      });
      const ct = res.headers.get("content-type") || "";
      const body = ct.includes("application/json") ? await res.json().catch(() => null) : await res.text().catch(() => null);
      console.log("loadMilestones res", projectId, res.status, body);
      if (!res.ok) throw new Error(body?.error || body || "Failed to load milestones");
      setMilestones(Array.isArray(body) ? body : []);
    } catch (err) {
      console.error("loadMilestones error:", err);
      setMilestones([]);
      showMsg(`❌ ${err.message}`);
    }
  }

  // Load payments for selected project
  async function loadPayments(projectId) {
    if (!projectId) {
      setPayments([]);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/payments/project/${projectId}`, {
        headers: apiHeaders(false),
      });
      const ct = res.headers.get("content-type") || "";
      const body = ct.includes("application/json") ? await res.json().catch(() => null) : await res.text().catch(() => null);
      console.log("loadPayments res", projectId, res.status, body);
      if (!res.ok) throw new Error(body?.error || body || "Failed to load payments");
      setPayments(Array.isArray(body) ? body : []);
    } catch (err) {
      console.error("loadPayments error:", err);
      setPayments([]);
      showMsg(`❌ ${err.message}`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadProjects();
  }, []);

  // when project selection changes, load milestones and payments
  useEffect(() => {
    if (selectedProject) {
      loadMilestones(selectedProject);
      loadPayments(selectedProject);
    } else {
      setMilestones([]);
      setPayments([]);
    }
    // reset form when switching projects
    setForm(() => ({ ...emptyForm, project_id: selectedProject || null }));
    setIsEditing(false);
    setShowForm(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject]);

  // Form helpers
  function openNewForm() {
    setForm({ ...emptyForm, project_id: selectedProject || null, payment_date: new Date().toISOString().slice(0, 10) });
    setIsEditing(false);
    setShowForm(true);
  }

  function openEditForm(payment) {
    setForm({
      id: payment.id,
      project_id: payment.project_id,
      milestone_id: payment.milestone_id,
      type: payment.type,
      amount: payment.amount != null ? String(payment.amount) : "",
      payment_date: payment.payment_date ? new Date(payment.payment_date).toISOString().slice(0,10) : new Date().toISOString().slice(0,10),
      notes: payment.notes || "",
      method: payment.method || "",
      category: payment.category || "",
      status: payment.status || "pending",
    });
    setIsEditing(true);
    setShowForm(true);
  }

  function handleFormChange(e) {
    const { name, value } = e.target;
    setForm((s) => ({ ...s, [name]: value }));
  }

  async function submitForm(e) {
    e?.preventDefault();
    if (!form.project_id) return showMsg("❌ Select a project first");
    setSaving(true);
    try {
      const payload = {
        project_id: Number(form.project_id),
        milestone_id: form.milestone_id ? Number(form.milestone_id) : null,
        type: form.type,
        amount: form.amount !== "" ? Number(form.amount) : 0,
        payment_date: form.payment_date || new Date().toISOString().slice(0,10),
        notes: form.notes || null,
        method: form.method || null,
        category: form.category || null,
        status: form.status || "pending",
      };

      if (isEditing && form.id) {
        const res = await fetch(`${API_BASE}/api/payments/${form.id}`, {
          method: "PUT",
          headers: apiHeaders(true),
          body: JSON.stringify(payload),
        });
        if (!res.ok) {
          const txt = await res.text().catch(() => "");
          throw new Error(txt || "Failed to update payment");
        }
        showMsg("✅ Payment updated");
      } else {
        const res = await fetch(`${API_BASE}/api/payments`, {
          method: "POST",
          headers: apiHeaders(true),
          body: JSON.stringify(payload),
        });
        if (!res.ok) {
          const txt = await res.text().catch(() => "");
          throw new Error(txt || "Failed to create payment");
        }
        showMsg("✅ Payment created");
      }

      // refresh payments and milestones
      await loadPayments(form.project_id);
      await loadMilestones(form.project_id);
      setShowForm(false);
      setIsEditing(false);
    } catch (err) {
      console.error("submitForm error:", err);
      showMsg(`❌ ${err.message}`);
    } finally {
      setSaving(false);
    }
  }

  async function removePayment(id) {
    if (!window.confirm("Delete this payment?")) return;
    try {
      const res = await fetch(`${API_BASE}/api/payments/${id}`, {
        method: "DELETE",
        headers: apiHeaders(false),
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(txt || "Failed to delete payment");
      }
      showMsg("✅ Payment deleted");
      await loadPayments(selectedProject);
      await loadMilestones(selectedProject);
    } catch (err) {
      console.error("removePayment error:", err);
      showMsg(`❌ ${err.message}`);
    }
  }

  async function togglePaymentStatus(payment) {
    const newStatus = payment.status === "paid" ? "pending" : "paid";
    try {
      const res = await fetch(`${API_BASE}/api/payments/${payment.id}`, {
        method: "PUT",
        headers: apiHeaders(true),
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(txt || "Failed to update payment status");
      }
      showMsg(`✅ Marked ${newStatus}`);
      await loadPayments(selectedProject);
    } catch (err) {
      console.error("togglePaymentStatus error:", err);
      showMsg(`❌ ${err.message}`);
    }
  }

  return (
    <div className="payments-page">
      <header className="payments-header">
        <h2>Payments</h2>

        <div className="payments-controls">
          <select
            value={selectedProject}
            onChange={(e) => {
              // keep state as number (or empty string)
              const v = e.target.value;
              setSelectedProject(v ? Number(v) : "");
            }}
          >
            <option value="">-- Select Project --</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} {p.deadline ? `(due ${new Date(p.deadline).toLocaleDateString()})` : ""}
              </option>
            ))}
          </select>

          <button
            className="btn primary"
            onClick={openNewForm}
            disabled={!selectedProject}
            title={selectedProject ? "Add payment" : "Select a project first"}
          >
            ➕ New Payment
          </button>
        </div>
      </header>

      {message && <div className="payments-msg">{message}</div>}

      <section className="payments-list">
        <h3>
          Payments {selectedProject ? `for project #${selectedProject}` : ""}
          {loading && <span className="muted"> — loading…</span>}
        </h3>

        <table className="payments-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Milestone</th>
              <th>Type</th>
              <th>Amount</th>
              <th>Payment Date</th>
              <th>Method</th>
              <th>Category</th>
              <th>Status</th>
              <th style={{ width: 180 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {payments.length === 0 ? (
              <tr>
                <td colSpan="9" className="empty">No payments</td>
              </tr>
            ) : (
              payments.map((p) => {
                const milestone = milestones.find((m) => Number(m.id) === Number(p.milestone_id));
                return (
                  <tr key={p.id}>
                    <td>{p.id}</td>
                    <td>{milestone ? milestone.title : (p.milestone_id ? `#${p.milestone_id}` : "—")}</td>
                    <td>{p.type}</td>
                    <td>{p.amount != null ? Number(p.amount).toFixed(2) : "-"}</td>
                    <td>{p.payment_date ? new Date(p.payment_date).toLocaleDateString() : "-"}</td>
                    <td>{p.method || "-"}</td>
                    <td>{p.category || "-"}</td>
                    <td>
                      <button
                        className={`status-btn ${p.status === "paid" ? "paid" : "pending"}`}
                        onClick={() => togglePaymentStatus(p)}
                      >
                        {p.status}
                      </button>
                    </td>
                    <td>
                      <button onClick={() => openEditForm(p)}>Edit</button>
                      <button onClick={() => removePayment(p.id)} className="danger">Delete</button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </section>

      {/* FORM MODAL */}
      {showForm && (
        <div className="payments-modal-overlay" onClick={() => setShowForm(false)}>
          <div className="payments-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{isEditing ? "Edit Payment" : "New Payment"}</h3>

            <form onSubmit={submitForm} className="payments-form">
              <label>
                Project
                <select name="project_id" value={form.project_id || ""} onChange={(e) => {
                  setForm((s) => ({ ...s, project_id: e.target.value ? Number(e.target.value) : null }));
                }} required>
                  <option value="">-- Select project --</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </label>

              <label>
                Milestone (optional)
                <select name="milestone_id" value={form.milestone_id || ""} onChange={(e) => setForm((s) => ({ ...s, milestone_id: e.target.value ? Number(e.target.value) : "" }))}>
                  <option value="">-- None --</option>
                  {milestones.map((m) => (
                    <option key={m.id} value={m.id}>{m.title} ({m.amount != null ? Number(m.amount).toFixed(2) : "—"})</option>
                  ))}
                </select>
              </label>

              <label>
                Type
                <select name="type" value={form.type} onChange={handleFormChange}>
                  <option value="income">Income</option>
                  <option value="expense">Expense</option>
                </select>
              </label>

              <label>
                Amount
                <input name="amount" type="number" step="0.01" value={form.amount} onChange={handleFormChange} required />
              </label>

              <label>
                Payment Date
                <input name="payment_date" type="date" value={form.payment_date} onChange={handleFormChange} />
              </label>

              <label>
                Method
                <input name="method" type="text" value={form.method} onChange={handleFormChange} />
              </label>

              <label>
                Category
                <input name="category" type="text" value={form.category} onChange={handleFormChange} />
              </label>

              <label>
                Status
                <select name="status" value={form.status} onChange={handleFormChange}>
                  <option value="pending">Pending</option>
                  <option value="paid">Paid</option>
                  <option value="failed">Failed</option>
                </select>
              </label>

              <label>
                Notes
                <textarea name="notes" value={form.notes} onChange={handleFormChange} />
              </label>

              <div className="payments-form-actions">
                <button type="submit" className="btn primary" disabled={saving}>
                  {saving ? (isEditing ? "Saving…" : "Creating…") : (isEditing ? "Save changes" : "Create payment")}
                </button>
                <button type="button" onClick={() => { setShowForm(false); setIsEditing(false); }} className="btn">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
